﻿<script setup lang="ts">
    import { computed, ref, watchEffect, watch } from 'vue';
    import { useSchemaEditStore } from '@/stores/schemaEditStore.ts';

    const store = useSchemaEditStore();

    type Schema = {
        Name: string;
        Identity: string;
    };

    type Props = {
        schemas: Schema[];
        schemasWithMapping: Set<string> | null;
        identity: string;
    };

    const selectedSchema = defineModel<Schema>();

    const props = defineProps<Props>();

    const isDropdownVisible = ref(false);

    const mappedSchemas = computed<Schema[]>(() => {
        if (!props.schemasWithMapping) {
            return [];
        }
        const schemasWithMapping = props.schemasWithMapping ?? new Set<string>();

        return props.schemas
            .filter(schema =>
                schema.Identity !== props.identity
                && schemasWithMapping.has(schema.Identity))
            .sort((a, b) => a.Name.localeCompare(b.Name));
    });

    const unmappedSchemas = computed<Schema[]>(() => {
        const schemasWithMapping = props.schemasWithMapping ?? new Set<string>();

        return props.schemas
            .filter(schema =>
                schema.Identity !== props.identity
                && !schemasWithMapping.has(schema.Identity))
            .sort((a, b) => a.Name.localeCompare(b.Name));
    });

    const selectSchema = (schema: Schema): void => {
        selectedSchema.value = schema;
        if (schema) {
            store.selectedSchema2 = schema.Identity;
        }
    };

    watchEffect(() => {
        // If only one schema has mapping, select it automatically
        if (!selectedSchema.value && mappedSchemas.value && mappedSchemas.value.length === 1) {
            selectedSchema.value = mappedSchemas.value[0];
        }
    });

    watch(() => props.schemasWithMapping, (newVal) => {
        // If the number of schemas with mapping is not 1, try set selectedSchema from store
        if (!selectedSchema.value
            && newVal
            && newVal.size !== 1
            && store.selectedSchema2
            && store.selectedSchema2 !== props.identity) {
            selectedSchema.value = props.schemas.find(schema => schema.Identity === store.selectedSchema2);
        }
    }, { immediate: true });
</script>

<template>
    <b-dropdown
        v-model="selectedSchema"
        max-height="60vh"
        :scrollable="true"
        class="dropdownForm"
        :triggers="['hover']"
        aria-role="list"
        :aria-hidden="!isDropdownVisible">
        <template #trigger>
            <b-button
                :label="selectedSchema?.Name ?? 'Select Schema 2'"
                class="button is-primary is-dropdown-trigger" />
        </template>
        <template v-if="mappedSchemas.length > 0">
            <b-dropdown-item
                class="is-disabled"
                aria-role="presentation">
                <strong>Schemas with mapping</strong>
            </b-dropdown-item>
            <b-dropdown-item
                v-for="schema in mappedSchemas"
                :key="schema.Identity"
                :value="schema"
                aria-role="listitem"
                class="indented-item"
                @click="selectSchema(schema)">
                {{ schema.Name }}
            </b-dropdown-item>
        </template>
        <template v-if="unmappedSchemas.length > 0">
            <b-dropdown-item
                class="is-disabled"
                aria-role="presentation">
                <strong>Schemas without mapping</strong>
            </b-dropdown-item>
            <b-dropdown-item
                v-for="schema in unmappedSchemas"
                :key="schema.Identity"
                :value="schema"
                aria-role="listitem"
                class="indented-item"
                @click="selectSchema(schema)">
                {{ schema.Name }}
            </b-dropdown-item>
        </template>
    </b-dropdown>
</template>

<style scoped>
.indented-item {
    padding-left: 30px; /* Adjust the value for desired indentation */
}
</style>
